import React, { useEffect, useMemo, useRef, useState } from 'react';



const EMPTY_TIMINGS = [];

const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');

// Keep measured speech cues; estimate only script tokens absent from the ASR.

function buildTimeline(narration, wordTimings, duration) {

  const tokens = [...narration.matchAll(/(\S+)(\s*)/g)];

  const script = tokens.map(token=>token[1]);

  // Estimate speech and pauses separately when measured words are unavailable.

  // Paragraph breaks give each narrated detail time to remain highlighted.

  const speechSeconds = .4;

  const pauses = tokens.map(([_,word,space])=>

    (/[.!?]$/.test(word)?.35:/[,;:]$/.test(word)?.12:0) + (/\n\s*\n/.test(space)?.65:0));

  const weights = pauses.map(pause=>speechSeconds+pause);

  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>

    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);

  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : weights.reduce((sum,weight)=>sum+weight,0);

  const mapped = Array(script.length).fill(null);

  if (spoken.length) {

    const rows = script.length + 1, columns = spoken.length + 1;

    const scores = new Uint16Array(rows * columns);

    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));

    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {

      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]

        ? scores[(i - 1) * columns + j - 1] + 1

        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);

    }

    let i = script.length, j = spoken.length;

    while (i > 0 && j > 0) {

      if (source[i - 1] && source[i - 1] === audio[j - 1]) {

        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;

      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;

      else j -= 1;

    }

  }

  const measured = mapped.filter(Boolean).length;

  for (let index = 0; index < mapped.length;) {

    if (mapped[index]) { index += 1; continue; }

    const from = index;

    while (index < mapped.length && !mapped[index]) index += 1;

    const start = from ? mapped[from - 1].end : 0;

    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);

    const totalWeight = weights.slice(from,index).reduce((sum,weight)=>sum+weight,0);

    const scale = totalWeight ? (end-start)/totalWeight : 0;

    let cursor = start;

    for (let k = from; k < index; k += 1) {

      mapped[k] = {start:cursor,end:cursor+speechSeconds*scale,measured:false};

      cursor += weights[k]*scale;

    }

  }

  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };

}

function wordOffset(narration, phrase) {

  const at = narration.indexOf(phrase);

  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);

  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;

}

function cueTime(narration, timeline, phrase) {

  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;

}

function activeIndex(items, now) {

  let index = 0;

  items.forEach((item, i) => { if (now >= item.start) index = i; });

  return index;

}

// Keep the same timing-input aliases and stable timeline as What Is Tribe.

function useNarrationTimeline(narration, wordTimings, duration) {

  const timingKey = JSON.stringify(Array.isArray(wordTimings)

    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);

  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);

}

// Read the audio clock each frame. Typing adds character boundaries so it

// stays in sync with speech, pause, seeking, and playback rate changes.

function useLessonClock({ time, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {

  const boundaries = useMemo(() => [...timeline.words.map(word => word.start), ...(timeline.animationBoundaries ?? [])].sort((a,b)=>a-b), [timeline]);

  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;

  const parentAnchor = useRef({ time: sourceTime, stamp: null });

  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });

  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });

  const boundaryAt = current => {

    let low = 0, high = boundaries.length;

    while (low < high) {

      const middle = (low + high) >>> 1;

      if (boundaries[middle] <= current + .005) low = middle + 1;

      else high = middle;

    }

    return low - 1;

  };

  useEffect(() => {

    const previous = parentAnchor.current;

    const stamp = performance.now();

    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;

    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);

    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;

    parentAnchor.current = { time: sourceTime, stamp };

    // The media element is authoritative when available; otherwise changes to

    // the parent's time keep the interpolated fallback clock anchored.

    if (!(audioElement ?? audioRef?.current) && seek) {

      controller.current.syncId += 1;

      controller.current.boundary = -2;

      controller.current.time = sourceTime;

      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,

        syncId: controller.current.syncId, seeking: true });

    }

  }, [sourceTime, audioElement, audioRef]);

  useEffect(() => {

    const media = audioElement ?? audioRef?.current;

    const playbackChanged = controller.current.playbackId !== playbackId;

    if (!media) {

      const stamp = performance.now();

      const anchor = parentAnchor.current;

      const heldTime = anchor.time + (controller.current.playing && anchor.stamp != null

        ? (stamp-anchor.stamp)/1000*controller.current.rate : 0);

      // Anchor pause/resume to the elapsed clock instead of resetting to zero.

      parentAnchor.current = {time:playbackChanged?sourceTime:heldTime,stamp};

    }

    let frame;

    const read = () => {

      const state = controller.current;

      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';

      const rate = media ? media.playbackRate || 1 : 1;

      const anchor = parentAnchor.current;

      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,

        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));

      const boundary = boundaryAt(current);

      return { current, boundary, playing, rate };

    };

    const update = (force = false, seek = false) => {

      const sample = read(), state = controller.current;

      const rateChanged = sample.rate !== state.rate;

      if (seek || rateChanged) state.syncId += 1;

      state.time = sample.current;

      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {

        state.boundary = sample.boundary;

        state.playing = sample.playing;

        state.rate = sample.rate;

        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,

          syncId: state.syncId, seeking: seek || rateChanged });

      }

      return sample.playing;

    };

    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };

    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };

    const stop = () => { cancelAnimationFrame(frame); update(true); };

    const seek = () => { update(true, true); };

    const rate = () => { update(true); };

    const sync = () => { update(); };

    if (media) {

      media.addEventListener('play', play); media.addEventListener('pause', stop);

      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);

      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);

    }

    if (controller.current.playbackId !== playbackId) {

      controller.current.playbackId = playbackId;

      controller.current.syncId += 1;

    }

    update(true);

    if (read().playing) frame = requestAnimationFrame(tick);

    return () => {

      cancelAnimationFrame(frame);

      if (media) {

        media.removeEventListener('play', play); media.removeEventListener('pause', stop);

        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);

        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);

      }

    };

  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);

  return clock;

}

// Phrase-cued motion matches What Is Tribe and Event Structure. Delays stay

// fixed between clock updates, so CSS completes the gentle rise smoothly.

function PhraseReveal({ as: Tag = 'h1', text, emphasis = [], clock, complete = false, delay = 0 }) {

  const words = text.split(/\s+/).filter(Boolean);

  const settled = useMemo(() => clock.seeking || (!clock.playing && clock.now > 0), [clock.syncId, text]);

  const strong = new Set();

  emphasis.forEach(phrase => {

    const offset = wordOffset(text, phrase);

    phrase.split(/\s+/).forEach((_, index) => strong.add(offset + index));

  });

  return <Tag aria-label={text}>{words.map((word, index) => <React.Fragment key={index}>

    <span className={`connections-reveal-word ${strong.has(index) ? 'is-emphasized' : ''}`} aria-hidden="true" style={{

      animationDelay: `${complete || settled ? -10 : (delay + index * .095) / clock.rate}s`,

      animationDuration: `${.28 / clock.rate}s`,

      animationPlayState: complete || clock.playing ? 'running' : 'paused',

    }}>{word}</span>{index < words.length - 1 ? ' ' : ''}

  </React.Fragment>)}</Tag>;

}





const SCENES = [

  { key: 'principle', cue: 'The second key concept', text: 'The second key concept: fuse guests.', emphasis: ['fuse guests.'] },

  { key: 'definition', cue: 'Fusing guests means', text: 'Find similarities. Spark a connection.', emphasis: ['similarities.', 'connection.'] },

  { key: 'ownership', cue: 'While much of the structure', text: 'Take it a step further.', emphasis: ['step further.'] },

  { key: 'listen', cue: 'As you move through your event', text: 'Listen for common ground.' },

  { key: 'walk', cue: 'When you find similarities', text: 'Walk them over.', emphasis: ['Walk them over.'] },

  { key: 'introduce', cue: 'make the introduction', text: 'Make the introduction.', emphasis: ['introduction.'] },

];



const CLUES = [

  { cue: 'where people are from', text: 'Where they’re from', detail: 'A hometown, neighborhood, or place they’ve lived.' },

  { cue: 'what they care about', text: 'What they care about', detail: 'An interest, a cause, or something they love.' },

  { cue: 'what energizes them', text: 'What energizes them', detail: 'The things they light up talking about.' },

];



function buildConnectionsCues(narration, timeline) {

  const scenes = SCENES.map(scene => ({ ...scene, start: cueTime(narration, timeline, scene.cue) }));

  const clues = CLUES.map(row => {

    const last = wordOffset(narration, row.cue) + row.cue.split(/\s+/).length - 1;

    return { ...row, start: cueTime(narration, timeline, row.cue), end: timeline.words[last]?.end ?? Infinity };

  });

  return { scenes, clues };

}



function ListeningClues({ rows, clock, complete }) {

  const latest = complete ? rows.length - 1 : rows.reduce((index, row, i) => clock.now >= row.start ? i : index, -1);

  const equal = complete || clock.now >= rows.at(-1).end;

  return <ol className={`connections-clues ${equal ? 'is-equal' : ''}`} aria-label="Clues to common ground">

    {rows.map((row, index) => {

      const visible = index <= latest;

      const focused = visible && !equal && index === latest;

      return <li key={row.cue} className={`connections-clue ${visible ? 'is-visible' : ''} ${focused ? 'is-focused' : ''} ${visible && !focused && !equal ? 'is-muted' : ''}`} aria-hidden={visible ? undefined : true} aria-current={focused ? 'step' : undefined}>

        <span className="connections-clue-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>

        {visible && <div className="connections-clue-copy" key={`${row.cue}-${clock.syncId}`}>

          <PhraseReveal as="h2" text={row.text} clock={clock} complete={complete} />

          <PhraseReveal as="p" text={row.detail} clock={clock} complete={complete} delay={.12} />

        </div>}

      </li>;

    })}

  </ol>;

}



// This lesson owns its visuals, narration metadata, and summary.

export default function HelpGuestsConnect(props = {}) {

  const narration = HelpGuestsConnect.parts[0].narration;

  const speechTimeline = useNarrationTimeline(narration, props.wordTimings ?? props.words ?? EMPTY_TIMINGS, props.duration ?? 0);

  const cues = useMemo(() => buildConnectionsCues(narration, speechTimeline), [narration, speechTimeline]);

  const timeline = useMemo(() => ({ ...speechTimeline, animationBoundaries: [cues.clues.at(-1).end] }), [speechTimeline, cues]);

  const clock = useLessonClock({ ...props, playbackId: `${props.playbackId ?? 0}:connections` }, timeline);

  const complete = props.audioStatus === 'Complete' || Boolean((props.audioElement ?? props.audioRef?.current)?.ended);

  const scene = cues.scenes[complete ? cues.scenes.length - 1 : activeIndex(cues.scenes, clock.now)];

  const listening = scene.key === 'listen';

  return <div className="host-background-slide host-connections-slide" aria-label={HelpGuestsConnect.title}>

    <style>{CONNECTIONS_STYLES}</style>

    <section className={`connections-scene ${listening ? 'is-listening' : 'is-caption'}`} data-scene={scene.key} data-timing-mode={timeline.mode}>

      <div className="connections-content">

        <div className="connections-headline" key={`${scene.key}-${clock.syncId}`}>

          <PhraseReveal text={scene.text} emphasis={scene.emphasis ?? []} clock={clock} complete={complete} />

        </div>

        {listening && <ListeningClues rows={cues.clues} clock={clock} complete={complete} />}

      </div>

    </section>

  </div>;

}



const CONNECTIONS_STYLES = `

@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap');

.host-connections-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;}

.host-connections-slide * {box-sizing:border-box;}

.host-connections-slide .connections-scene {display:flex;align-items:center;min-height:620px;padding:48px 44px;}

.host-connections-slide .connections-content {width:min(1000px,100%);margin:0 auto;}

.host-connections-slide .connections-headline h1 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(28px,3.2vw,40px);font-weight:700;line-height:1.25;letter-spacing:-.015em;}

.host-connections-slide .is-caption .connections-headline {max-width:800px;margin:auto;text-align:center;}

.host-connections-slide .is-listening .connections-headline {max-width:800px;margin:0 auto 52px;text-align:center;}

.host-connections-slide .connections-clues {display:grid;grid-template-columns:repeat(3,minmax(0,1fr));list-style:none;margin:0;padding:0;}

.host-connections-slide .connections-clue {min-width:0;min-height:280px;padding:0 clamp(20px,3vw,36px);opacity:0;transition:opacity 300ms ease;}

.host-connections-slide .connections-clue:first-child {padding-left:0;}

.host-connections-slide .connections-clue:last-child {padding-right:0;}

.host-connections-slide .connections-clue+.connections-clue {border-left:1px solid #dedede;}

.host-connections-slide .connections-clue.is-visible {opacity:1;}

.host-connections-slide .connections-clue.is-muted {opacity:.3;}

.host-connections-slide .connections-clue-number {display:block;margin:0 0 26px;padding-right:.08em;font-family:Merriweather,Georgia,serif;font-size:clamp(76px,8vw,104px);font-style:italic;font-weight:900;line-height:1;letter-spacing:-.08em;color:transparent;-webkit-text-stroke:1px #aaa;transition:color 300ms ease,-webkit-text-stroke-color 300ms ease;}

.host-connections-slide .is-focused .connections-clue-number,.host-connections-slide .is-equal .connections-clue-number {color:#111;-webkit-text-stroke-color:#111;}

.host-connections-slide .connections-clue h2 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(20px,2.2vw,25px);font-weight:700;line-height:1.3;letter-spacing:-.015em;}

.host-connections-slide .connections-clue p {max-width:280px;margin:14px 0 0;color:#666;font-size:14px;line-height:1.65;letter-spacing:-.01em;}

.host-connections-slide .connections-reveal-word {display:inline-block;opacity:0;transform:translateY(.16em);animation:connections-word-in 280ms cubic-bezier(.18,.84,.22,1) both;}

.host-connections-slide .connections-reveal-word.is-emphasized {font-weight:900;}

@keyframes connections-word-in {to{opacity:1;transform:translateY(0);}}

.host-training .ht-lesson-layout:has(.host-connections-slide) {display:block;padding:0;}

.host-training .ht-lesson-layout:has(.host-connections-slide)>.ht-lesson-copy {display:none;}

.host-training .ht-lesson-visual:has(.host-connections-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}

@media(max-width:700px){

 .host-connections-slide .connections-scene {min-height:530px;padding:32px 24px;}

 .host-connections-slide .connections-headline h1 {font-size:clamp(28px,7vw,34px);}

 .host-connections-slide .is-listening .connections-headline {margin-bottom:38px;}

 .host-connections-slide .connections-clue {min-height:250px;padding:0 14px;}

 .host-connections-slide .connections-clue-number {font-size:64px;margin-bottom:22px;}

 .host-connections-slide .connections-clue h2 {font-size:19px;}

 .host-connections-slide .connections-clue p {font-size:13px;}



}

@media(max-width:480px){

 .host-connections-slide .connections-clues {grid-template-columns:1fr;}

 .host-connections-slide .connections-clue,.host-connections-slide .connections-clue:first-child,.host-connections-slide .connections-clue:last-child {min-height:0;padding:28px 0;}

 .host-connections-slide .connections-clue:first-child {padding-top:0;}

 .host-connections-slide .connections-clue+.connections-clue {border-left:0;border-top:1px solid #dedede;}

 .host-connections-slide .connections-clue-number {font-size:72px;margin-bottom:20px;}

 .host-connections-slide .connections-clue h2 {font-size:23px;}

 .host-connections-slide .connections-clue p {max-width:100%;font-size:14px;}

}

@media(prefers-reduced-motion:reduce){

 .host-connections-slide .connections-reveal-word {animation:none!important;opacity:1;transform:none;}

 .host-connections-slide .connections-clue,.host-connections-slide .connections-clue-number {transition:none;}

}

`;



Object.assign(HelpGuestsConnect, {

  "slide": 9,

  "id": "connections",

  "title": "Help Guests Connect",

  "section": "Facilitation",

  "parts": [

    {

      "part": 0,

      "narration": "The second key concept is to fuse guests. Fusing guests means actively finding similarities between attendees and sparking connection between them. While much of the structure we’ve provided is already designed to do this, you need to take it a step further. As you move through your event, listen for clues like where people are from, what they care about, and what energizes them. When you find similarities, don’t just say, “You should go talk to that person.” Walk them over and make the introduction. That extra five seconds from you makes it way easier by breaking the discomfort of approaching someone new."

    }

  ],

  "summary": [

    "Listen for shared interests.",

    "Make the introduction yourself."

  ]

});
